import { useContext, useEffect, useState } from 'react';

import type { PlanningEntityType } from 'generated';
import { useRecoilState, useRecoilValue } from 'recoil';

import type { Event } from '../../types';
import type { EditAppointmentType } from 'RestAPIs/activities/types';

import { getAddressNotFound, getClientName, getHasWarnings } from '../../utils';

import {
  assignEmploymentEditAppointmentTypeAtom,
  dialogActiveAtom,
  selectedContextMenuOptionAtom,
  shouldShowPopoverDialogAtom
} from 'components/roster-portal/activity-planning/atoms';

import { Box, Tooltip } from '@mui/material';
import {
  PopoverWarningDialog,
  EventTooltip
} from 'components/roster-portal/activity-planning/components';

import { ChipPragmatic } from '../../TimelineView/AppointmentChip/ChipPragmatic';
import { PopOverConfirmationContext } from '../../PopOverConfirmationContext';

import { getZoomLevelProps } from '../../TimelineView/AppointmentChip/chip-zoom-level-props';
import { ZoomLevel as DetailZoomLevel } from '../../components/Header/ZoomLevelMenu.enums';
import { SignalRow } from '../../components/SignalRow';
import { ChipContextMenu } from './ChipContextMenu';
import dayjs from 'dayjs';

type Props = Event & {
  anchorEl?: HTMLElement;
  dialogActive?: boolean;
  handleRecurrenceSubmit?: (EditAppointmentType) => void;
  selected?: boolean;
  dragActionActive?: boolean;
  width?: number;
  startingPoint?: number;
  setHovered?: (hovered: boolean) => void;
  setDisabled?: (disabled: boolean) => void;
  onContextMenuClick?: (e: React.MouseEvent<HTMLElement>) => void;
  setAppointmentDropPositionX?: (object: Record<string, number> | null) => void;
  opacity?: number;
  isDragging?: boolean;
  hovered?: boolean;
  planningEntityType?: PlanningEntityType;
  /** Lifted to one subscription in `AppointmentsPragmatic` (~2 500 → 1). */
  detailZoomLevel?: DetailZoomLevel;
};

// Pragmatic port of `AppointmentChip` (presentation only); drag-source wrapper
// lives in `ConditionalDraggableChipPragmatic`.
export const AppointmentChipPragmatic = (props: Props) => {
  'use memo';
  const {
    id,
    activity,
    appointmentGrouping,
    clients,
    noteInternal,
    employees,
    isAssigned,
    isDragging,
    isEditable,
    isCreditable,
    isCredit,
    isOpen,
    isRecurring,
    isAbsent,
    hovered,
    location,
    warnings: {
      isOverQualified: overQualified,
      isUnderQualified: underQualified,
      startsTooEarly = false,
      startsTooLate = false,
      isInSync: isInSyncWithEcd = true,
      excludedEmployeeClientCombination = false,
      hasOverlap = false
    } = {},
    setHovered,
    setDisabled,
    timeWindow,
    width,
    selected,
    opacity,
    schedulingLabel,
    start,
    detailZoomLevel: detailZoomLevelProp
  } = props;
  const client = clients?.[0];

  const hasWarnings = getHasWarnings({
    startsTooEarly,
    startsTooLate,
    isInSyncWithEcd,
    excludedEmployeeClientCombination,
    isUrgent: !!timeWindow?.isUrgent,
    hasOverlap
  });

  const addressNotFound = getAddressNotFound(location, activity);

  const [anchorEl, setAnchorEl] = useState(null);

  const dialogActive = useRecoilValue(dialogActiveAtom);

  const [
    {
      id: popoverOccurenceId,
      isUnassignment,
      scheduleStart: popoverScheduleStart,
      isAssignEmployment
    },
    setShouldShowPopoverDialog
  ] = useRecoilState(shouldShowPopoverDialogAtom);

  const [, setAssignEmploymentEditAppointmentType] = useRecoilState<
    EditAppointmentType | null | undefined
  >(assignEmploymentEditAppointmentTypeAtom);
  const [, setSelectedContextMenuOption] = useRecoilState(
    selectedContextMenuOptionAtom
  );

  const showPopoverDialog =
    popoverOccurenceId === id &&
    dayjs(popoverScheduleStart).isValid() &&
    popoverScheduleStart === dayjs(start).format('YYYY-MM-DD');

  const detailZoomLevel = detailZoomLevelProp ?? DetailZoomLevel.STANDARD;
  const { showSignals, signalThreshold } = getZoomLevelProps(
    true,
    detailZoomLevel
  );

  const { onClose, onHandleAppointmentConfirmation } = useContext(
    PopOverConfirmationContext
  );

  const isClient = activity?.clientRelatedType !== 'NON_CLIENT_RELATED';
  const clientName = getClientName(client) ?? 'Onbekend';

  const allowToolTip =
    ((!isDragging && hovered) || isAbsent) &&
    !dialogActive &&
    !showPopoverDialog &&
    !anchorEl;

  function handleContextMenuClick(e) {
    e.preventDefault();
    // ChipContextMenu lazy-mounts and self-closes when options are empty.
    setAnchorEl(e.currentTarget);
  }

  function handleContextMenuClose() {
    setAnchorEl(null);
    setHovered?.(false);
  }

  useEffect(() => {
    setDisabled?.(anchorEl || dialogActive || !!isDragging);
  }, [dialogActive, anchorEl, isDragging, setDisabled]);

  // Extracted so the conditional PopoverWarningDialog wrapper doesn't remount
  // ChipPragmatic and tear down its draggable() registration.
  const chipBody = (
    <Box
      id={id}
      sx={{
        opacity,
        userSelect: 'none'
      }}
    >
      <ChipPragmatic
        width={width as number}
        isAbsent={isAbsent}
        isRecurring={isRecurring}
        isClient={isClient}
        selected={!!selected}
        hasWarnings={hasWarnings}
        handleRightClick={handleContextMenuClick}
        detailZoomLevel={detailZoomLevel}
      >
        <Box display="flex" flexDirection="column">
          <Box
            sx={{
              whiteSpace: 'nowrap',
              fontSize: '12px',
              height: '18px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              textDecoration: isAbsent ? 'line-through' : 'none'
            }}
          >
            {isClient ? clientName : activity?.name}
          </Box>

          {showSignals && (
            <SignalRow
              schedulingLabel={schedulingLabel}
              addressNotFound={addressNotFound}
              noteInternal={noteInternal}
              excludedEmployeeClientCombination={
                excludedEmployeeClientCombination
              }
              hasOverlap={hasOverlap}
              isAbsent={isAbsent}
              isInSyncWithEcd={isInSyncWithEcd}
              isRecurring={isRecurring}
              locationType={location?.locationType}
              overQualified={overQualified}
              selected={selected}
              signalThreshold={signalThreshold}
              startsTooEarly={startsTooEarly}
              startsTooLate={startsTooLate}
              timeWindow={timeWindow}
              underQualified={underQualified}
              maxWidth={width}
              sx={{
                padding: 0
              }}
            />
          )}
        </Box>
      </ChipPragmatic>
    </Box>
  );

  return (
    <Tooltip
      arrow
      enterNextDelay={500}
      disableHoverListener={!allowToolTip}
      title={
        allowToolTip && (
          <EventTooltip
            event={props as Event}
            onMouseOver={() => setHovered?.(false)}
          />
        )
      }
    >
      <Box>
        {showPopoverDialog ? (
          <PopoverWarningDialog
            open
            onClose={() => {
              if (isAssignEmployment) {
                setShouldShowPopoverDialog({ id: '' });
                setSelectedContextMenuOption(null);
                return setAssignEmploymentEditAppointmentType(null);
              }

              onClose?.();
              setShouldShowPopoverDialog({ id: '' });
            }}
            onClickAppointment={(editAppointmentType: EditAppointmentType) => {
              if (isAssignEmployment) {
                setShouldShowPopoverDialog({ id: '' });
                return setAssignEmploymentEditAppointmentType(
                  editAppointmentType
                );
              }

              onHandleAppointmentConfirmation?.(
                editAppointmentType,
                isUnassignment
              );
              setShouldShowPopoverDialog({ id: '' });
            }}
          >
            {chipBody}
          </PopoverWarningDialog>
        ) : (
          chipBody
        )}
        {anchorEl && (
          <ChipContextMenu
            anchorEl={anchorEl}
            eventId={id}
            clientId={clients?.[0]?.id}
            schedulingLabel={schedulingLabel}
            isEditable={!!isEditable}
            isAbsent={!!isAbsent}
            eventIsOpen={!!isOpen}
            isAssigned={!!isAssigned}
            hasAppointmentGrouping={!!appointmentGrouping}
            hasEmployee={!!employees?.[0]}
            isCreditable={!!isCreditable}
            isCredit={!!isCredit}
            onClose={handleContextMenuClose}
          />
        )}
      </Box>
    </Tooltip>
  );
};
